package org.teddapp.project.ui.backup

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import org.teddapp.project.db.nowMillis
import org.teddapp.project.sync.SyncStatus
import org.teddapp.project.sync.SyncTrigger
import org.teddapp.project.sync.SyncUiState
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.settings.LocalSupabaseConnector
import org.teddapp.project.ui.settings.LocalSyncBindings
import org.teddapp.project.ui.settings.SettingsDrawerHost
import org.teddapp.project.ui.settings.SupabaseConnector
import org.teddapp.project.ui.settings.SyncBindings
import org.teddapp.project.ui.settings.rememberSettingsDrawer
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddScreenHeader
import org.teddapp.project.ui.TeddEmptyState
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn

/**
 * Sync et sauvegarde, un seul écran pour toutes les plateformes.
 *
 * L'historique ne contient que les vraies exécutions du SyncManager : s'il est vide,
 * l'écran le dit. Les exports renvoient le chemin du fichier écrit, ou null si annulé.
 *
 * @param onExportJson reçoit un nom de fichier suggéré, renvoie le chemin écrit ou null.
 */
@Composable
fun BackupScreen(
    onNavigate: (Section) -> Unit = {},
    onExportJson: ((suggestedName: String) -> String?)? = null,
    onExportSql: ((suggestedName: String) -> String?)? = null,
    db: TeddDatabase? = null,
    modifier: Modifier = Modifier,
    /** Onglet ouvert à l'arrivée, par sa clé (« overview », « history », « tables », « export », « modules », « supabase »). */
    initialTab: String = "overview",
) {
    val data = rememberBackupData(db)
    val exports = remember(onExportJson, onExportSql) { BackupExports(onExportJson, onExportSql) }
    WithWindowClass(modifier = modifier) { wc ->
        when (wc) {
            WindowClass.Compact -> BackupCompactLayout(data, exports, onNavigate)
            else -> BackupExpandedLayout(data, exports, onNavigate, initialTab)
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Données dérivées de l'état de sync et de la base
// ──────────────────────────────────────────────────────────────────────
internal class BackupData(
    val bindings: SyncBindings?,
    val connector: SupabaseConnector?,
    val syncState: SyncUiState,
    val history: List<BackupEntry>,
    val gap: BackupGap,
    val monthCount: Int,
    val hoursSince: Double,
    val domainStats: List<BackupDomainStats>,
    val settings: BackupSettings,
    val applySettings: (BackupSettings) -> Unit,
) {
    val cloudAvailable: Boolean get() = bindings != null
    val syncing: Boolean get() = syncState.status is SyncStatus.Syncing
    val lastOk: BackupEntry? get() = history.firstOrNull { it.status == BackupStatus.Success }
    val modules: List<BackupModule> get() = if (domainStats.isNotEmpty()) domainStats.toUiModules() else emptyList()
    val totalKb: Int get() = domainStats.sumOf { it.sizeKb }
    val totalRows: Int get() = domainStats.sumOf { it.rowCount }

    fun runCloudSync() {
        if (bindings != null && syncState.enabled) bindings.onSyncNow()
    }
}

@Composable
private fun rememberBackupData(db: TeddDatabase?): BackupData {
    val appSettings = LocalAppSettings.current
    val bindings = LocalSyncBindings.current
    val connector = LocalSupabaseConnector.current
    val serverConnected = org.teddapp.project.server.LocalServerSession.current?.connected == true
    val syncState by (
        bindings?.state ?: remember {
            MutableStateFlow(
                SyncUiState(enabled = false, status = SyncStatus.Idle, lastSyncAt = null, pendingChanges = 0, errorMessage = null),
            )
        }
        ).collectAsState()

    val history = remember(syncState.history, serverConnected) {
        syncState.history.map { run ->
            BackupEntry(
                id = run.id,
                date = epochToIsoMinutes(run.startedAt),
                trigger = when (run.trigger) {
                    SyncTrigger.Manual -> BackupTrigger.Manual
                    SyncTrigger.Auto, SyncTrigger.BootCatchup -> BackupTrigger.AutoCloud
                },
                format = BackupFormat.Json,
                sizeKb = run.pushedCount,
                // Une table refusée après des milliers de lignes acceptées : partielle, pas échouée.
                status = when {
                    run.success -> BackupStatus.Success
                    run.pushedCount > 0 -> BackupStatus.Partial
                    else -> BackupStatus.Failed
                },
                destination = if (serverConnected) BackupDestination.Server else BackupDestination.Supabase,
                durationSec = run.durationMs / 1000.0,
                modulesCount = 0,
                note = run.errorMessage,
            )
        }
    }
    val cloudAvailable = bindings != null
    val gap = remember(syncState.lastSyncAt, cloudAvailable) {
        computeRealGap(cloudAvailable = cloudAvailable, lastSyncAt = syncState.lastSyncAt, now = nowMillis())
    }
    val monthCount = remember(syncState.history) { successesThisMonth(syncState.history, nowMillis()) }
    val hoursSince = remember(syncState.lastSyncAt) {
        syncState.lastSyncAt?.let { (nowMillis() - it) / 3_600_000.0 } ?: Double.POSITIVE_INFINITY
    }
    val domainStats = remember(db, syncState.pendingChanges, syncState.lastSyncAt) {
        if (db != null) computeDomainStats(db) else emptyList()
    }
    val settings = BackupSettings(
        autoBackup = appSettings.backupAuto,
        frequency = BackupFrequency.fromKey(appSettings.backupFrequency),
    )
    val applySettings: (BackupSettings) -> Unit = { s ->
        if (s.autoBackup != appSettings.backupAuto) appSettings.applyBackupAuto(s.autoBackup)
        if (s.frequency.key != appSettings.backupFrequency) appSettings.applyBackupFrequency(s.frequency.key)
    }
    return BackupData(bindings, connector, syncState, history, gap, monthCount, hoursSince, domainStats, settings, applySettings)
}

/** Exports fichier : renvoient un message pour l'utilisateur (chemin écrit, ou annulation). */
internal class BackupExports(
    private val json: ((String) -> String?)?,
    private val sql: ((String) -> String?)?,
) {
    val jsonAvailable: Boolean get() = json != null
    val sqlAvailable: Boolean get() = sql != null

    fun run(format: BackupFormat): String {
        val ts = epochToIsoMinutes(nowMillis()).replace(':', '-')
        val path = when (format) {
            BackupFormat.Json -> json?.invoke("tedd-backup-$ts.json")
            BackupFormat.Sql -> sql?.invoke("tedd-backup-$ts.sql")
        }
        val label = if (format == BackupFormat.Json) "JSON" else "SQL"
        return path?.let { "Sauvegarde $label :\n$it" } ?: "Export annulé"
    }
}

// ──────────────────────────────────────────────────────────────────────
// Étendu (et moyen) : rail + barre latérale + onglets
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun BackupExpandedLayout(data: BackupData, exports: BackupExports, onNavigate: (Section) -> Unit, initialTab: String) {
    val drawer = rememberSettingsDrawer()
    var tab by remember { mutableStateOf(BackupTab.entries.firstOrNull { it.key == initialTab } ?: BackupTab.Overview) }
    var bannerDismissed by remember { mutableStateOf(false) }
    var feedback by remember { mutableStateOf<String?>(null) }
    val state = data.syncState

    SettingsDrawerHost(drawer, onNavigate = onNavigate) {
        Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            Row(modifier = Modifier.fillMaxSize()) {
                TeddNavRail(active = Section.Backup, onNavigate = onNavigate, onSettingsClick = drawer::open)
                BackupSidebar(
                    tab = tab,
                    onTabChange = { tab = it },
                    historyCount = data.history.size,
                    cloudConnected = data.cloudAvailable && state.enabled,
                    projectUrl = data.bindings?.projectUrl,
                    syncEnabled = state.enabled,
                    onToggleSync = { data.bindings?.onToggle(it) },
                    syncAvailable = data.cloudAvailable,
                    pendingChanges = state.pendingChanges,
                    syncing = data.syncing,
                )
                Column(modifier = Modifier.weight(1f).fillMaxHeight()) {
                    // Le bandeau dit qu'une synchronisation tarde ou a échoué ; il reste
                    // au-dessus du contenu, quel que soit l'onglet.
                    val syncErr = state.errorMessage
                    val gap = when {
                        bannerDismissed -> null
                        syncErr != null -> BackupGap(BackupGapLevel.Danger, "La dernière synchronisation a échoué : $syncErr")
                        data.gap.level != BackupGapLevel.Ok -> data.gap
                        else -> null
                    }
                    if (gap != null) {
                        Box(modifier = Modifier.padding(start = TeddSpace.S8, end = TeddSpace.S8, top = TeddSpace.S4)) {
                            GapBanner(gap = gap, onDismiss = { bannerDismissed = true }, onRunNow = data::runCloudSync)
                        }
                    }
                    Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                        when (tab) {
                            BackupTab.Overview -> OverviewTab(
                                history = data.history,
                                lastOk = data.lastOk,
                                monthCount = data.monthCount,
                                hoursSince = data.hoursSince,
                                totalKb = data.totalKb,
                                modules = data.modules,
                                settings = data.settings,
                                cloudAvailable = data.cloudAvailable,
                                cloudEnabled = state.enabled,
                                lastSyncAt = state.lastSyncAt,
                                onSettingsChange = data.applySettings,
                                onRunBackup = { data.runCloudSync() },
                            )
                            BackupTab.History -> HistoryTab(data.history)
                            BackupTab.Tables -> SyncTablesDashboard(
                                state = state,
                                cloudAvailable = data.cloudAvailable && state.enabled,
                                onCheck = { data.bindings?.onCheckRemote() },
                                onSyncNow = data::runCloudSync,
                                modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).then(TabPadding),
                            )
                            BackupTab.Export -> ExportTab(
                                modules = data.modules,
                                totalKb = data.totalKb,
                                cloudAvailable = data.cloudAvailable && state.enabled,
                                onSyncCloud = data::runCloudSync,
                                onDownload = { format -> feedback = exports.run(format) },
                            )
                            BackupTab.Modules -> ModulesTab(modules = data.modules)
                            BackupTab.Supabase -> SupabasePane(data.connector)
                        }
                    }
                }
            }
            ExportFeedbackToast(feedback, onExpire = { feedback = null }, bottomPadding = 28.dp)
        }
    }
}

@Composable
private fun SupabasePane(connector: SupabaseConnector?) {
    val connected = connector?.isConnected == true
    Column(
        modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).then(TabPadding),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S6),
    ) {
        TeddScreenHeader(
            title = "Supabase",
            subtitle = if (connected) {
                "Ton projet est relié. Tu peux changer d'identifiants ou te déconnecter."
            } else {
                "Relie ton projet pour synchroniser l'ordinateur et le téléphone. Rien d'obligatoire : " +
                    "tout fonctionne en local, et l'onglet Exporter sort la base à tout moment."
            },
        )
        if (connector != null) {
            SupabaseConnectForm(connector = connector, modifier = Modifier.widthIn(max = 560.dp))
        } else {
            TeddEmptyState(
                title = "Connexion indisponible ici",
                message = "Cette plateforme ne sait pas se relier à Supabase.",
                icon = "lock",
            )
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Compact : une colonne de cartes + barre du bas
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun BackupCompactLayout(data: BackupData, exports: BackupExports, onNavigate: (Section) -> Unit) {
    val appSettings = LocalAppSettings.current
    var feedback by remember { mutableStateOf<String?>(null) }
    val state = data.syncState
    val title = when {
        data.syncing -> "Synchronisation…"
        data.cloudAvailable && state.tables.any { !it.healthy } -> "Des écarts avec le serveur"
        data.cloudAvailable && data.gap.level == BackupGapLevel.Warning -> "Sync en retard"
        data.cloudAvailable && data.lastOk == null -> "Prêt à synchroniser"
        else -> "Tes données sont à jour"
    }

    Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
        Column(modifier = Modifier.fillMaxSize()) {
            PhoneBackupHeader(title = title)
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 18.dp)
                    .padding(bottom = 24.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                if (data.cloudAvailable && data.gap.level != BackupGapLevel.Ok) GapAlertCard(gap = data.gap)
                StatusHeroCard(
                    lastOk = data.lastOk,
                    monthCount = data.monthCount,
                    totalKb = data.totalKb,
                    totalRows = data.totalRows,
                    cloudConnected = data.cloudAvailable && state.enabled,
                    isLocalOnly = !data.cloudAvailable,
                    pendingChanges = state.pendingChanges,
                    syncing = data.syncing,
                    onSyncNow = { data.bindings?.onSyncNow() },
                )
                if (data.cloudAvailable) {
                    SyncTablesDashboard(
                        state = state,
                        cloudAvailable = state.enabled,
                        onCheck = { data.bindings?.onCheckRemote() },
                        onSyncNow = { data.bindings?.onSyncNow() },
                        compact = true,
                    )
                }
                ExportGrid(
                    onJsonExport = { feedback = exports.run(BackupFormat.Json) },
                    onSqlExport = { feedback = exports.run(BackupFormat.Sql) },
                    jsonAvailable = exports.jsonAvailable,
                    sqlAvailable = exports.sqlAvailable,
                )
                AutoSyncToggleCard(on = appSettings.backupAuto, onChange = appSettings::applyBackupAuto)
                data.connector?.let { connector ->
                    if (data.cloudAvailable) {
                        ConnectionCompactCard(connector = connector)
                    } else {
                        CloudOptionalHint()
                        SupabaseConnectForm(connector = connector)
                    }
                }
                HistorySection(history = data.history, cloudAvailable = data.cloudAvailable)
            }
            TeddBottomNav(active = Section.Backup, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
        }
        ExportFeedbackToast(feedback, onExpire = { feedback = null }, bottomPadding = 96.dp)
    }
}

// ──────────────────────────────────────────────────────────────────────
// Toast d'export, commun
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun androidx.compose.foundation.layout.BoxScope.ExportFeedbackToast(
    message: String?,
    onExpire: () -> Unit,
    bottomPadding: androidx.compose.ui.unit.Dp,
) {
    LaunchedEffect(message) {
        if (message != null) {
            delay(4_000L)
            onExpire()
        }
    }
    if (message != null) {
        Box(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .padding(bottom = bottomPadding, start = 16.dp, end = 16.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Ink, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 14.dp, vertical = 12.dp),
        ) {
            Text(text = message, color = TeddTokens.Bg, style = TeddType.Caption)
        }
    }
}
